import { Component, HostListener, computed, effect } from '@angular/core';

import { CommonModule } from '@angular/common';
import { SharedService } from './services/shared.service';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { EncryptionService } from './services/encryption.service';
import { AiAgentService } from './services/ai-agent.service';
import { LanguageService } from './services/language.service';
import { environment } from '../environments/environment';
import { ToastComponent } from '@eleapi-ng/core';
import { AgencyService } from './services/agency.service';
import AgencyDetails from './models/agency-details';

interface DecryptedPayload {
  customerId: string;
  agencyId: string;
  accessToken: string;
  language: string;
  brand: string;
  returnUrl?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    ToastComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})

export class AppComponent {
  title = 'ai-agent';
  isLoaded = false;
  staticText: any;

  /**
   * Listen to messages sent from the parent window (i.e., iframe communication).
   * Used to receive token and language settings securely.
   */
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    const { type, encryptedPayload } = event.data;
    if (type === 'TOKEN_RESPONSE') {
      this.saveToken(encryptedPayload);
    }
  }

  renderChecker: any;
  render = false;

  readonly aiAgents = computed(() => this.aiAgentService.aiAgents());

  containsId = false;

  constructor(
    private languageService: LanguageService,
    private sharedService: SharedService,
    private aiAgentService: AiAgentService,
    private router: Router,
    private encryptionService: EncryptionService,
    private route: ActivatedRoute,
    private agencyService: AgencyService
  ) {
    /**
     * Request encrypted token and language from the parent application (iframe host).
     */
    /**
     * Request encrypted token and language from the parent application (iframe host).
     */
    window.parent.postMessage({
      type: 'REQUEST_TOKEN'
    }, '*');

    if (!environment.production && environment.devHosting.devToken) {
      this.sendEncryptedToken(window, '*');
    }

    this.setContainsId(this.route);

    // Handle in-app navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setContainsId(this.route);
      });

    let retryCount = 0;
    const maxRetries = 20;

    this.renderChecker = setInterval(() => {
      if (this.render && this.renderChecker) {
        clearInterval(this.renderChecker);
        return;
      }

      if (retryCount >= maxRetries) {
        clearInterval(this.renderChecker);
        console.warn('Max retries reached. LanguageService static text not loaded.');
        return;
      }

      retryCount++;

      const staticTextData = this.languageService.getStaticText().agentIntegration;

      this.render = !!Object.keys(staticTextData || {}).length;

      if (this.render) {
        this.staticText = JSON.parse(JSON.stringify(staticTextData));
      }
    }, 400);
  }

  ngOnDestroy(): void {
    if (this.renderChecker) {
      clearInterval(this.renderChecker);
    }
  }

  /**
   * To hide the navbar if the resource id is present for details page
   * @param route The activated route
   */
  private setContainsId(route: ActivatedRoute) {
    let child = route;
    while (child.firstChild) {
      child = child.firstChild;
    }

    const id = child.snapshot.paramMap.get('collectionId');
    this.containsId = !!id;
  }
  /**
   * Save decrypted token and language into localStorage.
   * Called after receiving and decrypting data from parent iframe.
   */
  saveToken = async (encryptedPayload: string) => {
    try {
      const result = await this.encryptionService.decrypt(encryptedPayload, environment.decryptKey);
      const { accessToken, language, brand, returnUrl, agencyId } = JSON.parse(result) as DecryptedPayload;
      this.aiAgentService.whitelabelAgencyId.set(agencyId || null);
      try {
        const response: AgencyDetails = await this.agencyService.getAgencyDetailsCustomer(agencyId || '');
        this.agencyService.setAgencyDetails(response);
      } catch (error) {
        console.error('Failed to fetch agency details:', error);
      }

      localStorage.setItem('token', accessToken);
      localStorage.setItem('language', language);
      sessionStorage.setItem('brand', brand);

      // Fetch ai agents and set them
      try {
        this.sharedService.loader('start');
      } catch (err) {
        console.error('Failed to load ai agents:', err);
      } finally {
        this.sharedService.loader('stop');
      }

      this.languageService.fetchStaticText();

      const langValue = JSON.parse(language)?.value || 'en';
      this.sharedService.updatePlatformDirection(langValue);

      // Signal to store the whitelabel agency ID
      this.aiAgentService.isWhiteLabel.set(
        this.aiAgentService.whitelabelAgencyId() !== environment.rootAgencyId
      );

      const staticTextData = this.languageService.getStaticText() || {};

      this.staticText = staticTextData;

      if (returnUrl) {
        const credentialId = returnUrl.split('?')[1].split('=')[1];
        const url = returnUrl.split('?')[0];

        this.router.navigate([url], { queryParams: { credentialId } });
      }
    } catch (error) {
      console.error('Decryption error:', error);
    } finally {
      this.isLoaded = true;
      this.sharedService.loader('stop');
    }
  };

  async encrypt(data: any, password: string) {
    const key = await this.getKey(password);

    const iv = crypto.getRandomValues(new Uint8Array(12)); // Generate IV
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(data))
    );

    return btoa(String.fromCharCode(...iv, ...new Uint8Array(encrypted))); // Encode IV + data
  }

  async getKey(password: string): Promise<CryptoKey> {
    const key = crypto.subtle.importKey(
      'raw',
      await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password)),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );

    return key;
  }

  sendEncryptedToken(targetWindow: Window, targetOrigin: string) {

    const token = environment.devHosting.devToken;

    const tokenPayload = JSON.stringify({
      customerId: environment.devHosting.customerId,
      agencyId: environment.devHosting.agencyId,
      accessToken: token,
      language: environment.devHosting.language,
      brand: environment.devHosting.brand
    });

    this.encrypt(tokenPayload, environment.decryptKey)
      .then(data => {
        if (targetWindow) {
          targetWindow.postMessage({
            type: 'TOKEN_RESPONSE',
            encryptedPayload: data
          }, targetOrigin);
        }
      });
  }
}
